import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api/axiosConfig";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, PackageSearch,
  Store, BadgeCheck, Percent, Eye,
} from "lucide-react";
import { getImageUrl } from "../utils/getImageUrl";
import MobileBottomBar from "../components/MobileBottomBar";
import ProductQuickViewModal from "../components/ProductQuickViewModal";
import CartQtyControl from "../components/CartQtyControl";

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:4000/api")
  .replace(/\/api\/?$/, "");

function imgUrl(path) {
  if (!path) return null;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${API_BASE}/${path.replace(/^\//, "")}`;
}

function normalize(obj) {
  if (!obj) return obj;
  return { ...obj, _id: obj._id ?? obj.id };
}

export default function EventProducts() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickView, setQuickView] = useState(null);

  useEffect(() => {
    API.get(`/events/${id}`)
      .then((res) => {
        const ev = res.data?.data?.event ?? res.data?.event ?? res.data;
        setEvent(ev ?? null);
      })
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7f8f9] flex items-center justify-center">
        <Loader2 size={32} className="text-supernova-500 animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#f7f8f9] flex flex-col items-center justify-center gap-4 px-6 text-center">
        <PackageSearch size={48} className="text-gray-300" />
        <p className="text-gray-500 font-semibold">Evento no encontrado</p>
        <button
          onClick={() => navigate(-1)}
          className="px-5 py-2 bg-supernova-500 text-white rounded-full text-sm font-bold"
        >
          Volver
        </button>
      </div>
    );
  }

  const bannerSrc = imgUrl(event.bannerBase64);
  const sectionLabel = event.productsButtonLabel || "Productos participantes";

  const products = (event.sponsors ?? []).flatMap((s) =>
    (s.products ?? [])
      .map((sp) => ({ ...sp.product, _sponsor: s }))
      .filter((p) => p && (p.id || p._id))
  );

  return (
    <div className="min-h-screen bg-[#f7f8f9] pb-28 lg:pb-10">

      {/* ── Header banner ─────────────────────────────────────────────────── */}
      <div className="relative w-full h-52 sm:h-64 bg-gray-200 overflow-hidden">
        {bannerSrc
          ? <img src={bannerSrc} alt={event.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-gradient-to-br from-supernova-400 to-supernova-600 flex items-center justify-center">
              <span className="text-white text-5xl">🏆</span>
            </div>
        }
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 flex items-center justify-center w-9 h-9 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full transition"
        >
          <ArrowLeft size={18} className="text-white" />
        </button>

        {/* Title */}
        <div className="absolute bottom-4 left-5 right-5 text-white">
          <p className="font-black text-xl leading-tight drop-shadow-md">{event.title}</p>
          <p className="text-white/70 text-xs mt-1">{sectionLabel}</p>
        </div>
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 pt-5">

        {/* Count pill */}
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-supernova-500 rounded-full shrink-0" />
          <h2 className="text-base font-black text-gray-900">
            {products.length} producto{products.length !== 1 ? "s" : ""}
          </h2>
        </div>

        {products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center">
              <PackageSearch size={36} className="text-gray-300" />
            </div>
            <p className="text-sm font-bold text-gray-500">
              Este evento no tiene productos registrados
            </p>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {products.map((product) => {
              const pid = product.id ?? product._id;
              const discount = product.offerPrice
                ? Math.round(((Number(product.price) - Number(product.offerPrice)) / Number(product.price)) * 100)
                : null;

              return (
                <motion.div
                  key={pid}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -3 }}
                  className="bg-white rounded-2xl overflow-hidden flex flex-col"
                  style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
                >
                  {/* Image */}
                  <div
                    onClick={() => navigate(`/product/${pid}`)}
                    className="relative h-36 bg-gray-50 overflow-hidden cursor-pointer"
                  >
                    {discount && (
                      <div className="absolute top-2 left-2 z-10 flex items-center gap-0.5 bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow">
                        <Percent size={8} /> {discount}% OFF
                      </div>
                    )}
                    {product.images?.[0]
                      ? <img
                          src={getImageUrl(product.images[0])}
                          alt={product.title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                      : <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <Store size={28} className="text-gray-300" />
                        </div>
                    }
                    <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>

                  {/* Info */}
                  <div className="p-3 flex flex-col flex-1">
                    <p
                      onClick={() => navigate(`/product/${pid}`)}
                      className="text-[11px] font-bold text-gray-800 line-clamp-2 leading-snug mb-1 cursor-pointer"
                    >
                      {product.title}
                    </p>

                    {/* Restaurant name */}
                    {product.company?.name && (
                      <div
                        onClick={() => navigate(`/company/${product.company.id ?? product.company._id ?? product.companyId}`)}
                        className="flex items-center gap-1 text-[10px] text-gray-400 mb-2 cursor-pointer hover:text-supernova-600 transition-colors"
                      >
                        <Store size={9} className="shrink-0" />
                        <span className="truncate font-medium">{product.company.name}</span>
                        {product.company.verified && <BadgeCheck size={9} className="text-blue-500 shrink-0" />}
                      </div>
                    )}

                    {/* Price */}
                    {product.offerPrice ? (
                      <div className="flex items-baseline gap-1.5 mb-2.5">
                        <span className="text-[10px] text-gray-400 line-through">
                          ${Number(product.price).toLocaleString("es-CO")}
                        </span>
                        <span className="text-sm font-black text-supernova-600">
                          ${Number(product.offerPrice).toLocaleString("es-CO")}
                        </span>
                      </div>
                    ) : (
                      <p className="text-sm font-black text-gray-900 mb-2.5">
                        ${Number(product.price).toLocaleString("es-CO")}
                      </p>
                    )}

                    {/* Acciones */}
                    <div className="flex items-center gap-1.5 mt-auto">
                      <button
                        type="button"
                        onClick={() => setQuickView(normalize(product))}
                        className="p-2 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition shrink-0"
                        title="Ver descripción"
                      >
                        <Eye size={13} className="text-gray-500" />
                      </button>
                      <CartQtyControl
                        product={normalize(product)}
                        size="sm"
                        addClassName="bg-supernova-500 hover:bg-supernova-600 text-white shadow-sm"
                      />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <MobileBottomBar />

      {quickView && (
        <ProductQuickViewModal product={quickView} onClose={() => setQuickView(null)} />
      )}
    </div>
  );
}
